import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import identityManager from "@arcgis/core/identity/IdentityManager";

export default {
    namespaced: true,

    state: {
        items: [],
        selectedItem: null,
        securePanoFeatureLayer: null,
        publicPanoFeatureLayer: null,
        unitInfoFeatureLayer: null,
        poiInfoFeatureLayer: null,
        poiInitialSearchDistance: null,
        poiMaxSearchDistance: null,
        poiTypes: null,
        activeQueryCnt: 0
    },

    actions: {
        // eslint-disable-next-line no-unused-vars
        initialize({dispatch}) {
            console.info('Pano module store initializing...')

            this.securePanoFeatureLayer = new FeatureLayer({
                url: "https://services.gis.msu.edu/agsent01/rest/services/IPFFacilitiesInformation/PanoramicPhotos_MIL/MapServer",
                layerId: 4
            })

            this.publicPanoFeatureLayer = new FeatureLayer({
                url: "https://services.gis.msu.edu/agsent01/rest/services/IPFFacilitiesInformation/PanoramicPublicPhotos_MIL/MapServer",
                layerId: 0
            })

            this.unitInfoFeatureLayer = new FeatureLayer({
                url: 'https://services.gis.msu.edu/agsent04/rest/services/Indoors/IndoorsCorePublic_MIL/FeatureServer',
                layerId: 5
            })

            this.poiInfoFeatureLayer = new FeatureLayer({
                url: 'https://services.gis.msu.edu/ags-wayfinding/rest/services/WayfindingResources/MapServer',
                layerId: 1
            })

            this.poiTypes = ['POI', 'BUILDING', 'BUILDINGPART', 'PARKINGRAMP', 'PARKINGLOT']
            this.poiInitialSearchDistance = 100
            this.poiMaxSearchDistance = 800
            this.activeQueryCnt = 0

            console.info('Pano module store initialized.')
        },

        // eslint-disable-next-line no-unused-vars
        getItemFromGlobalId({dispatch, commit}, globalId) {
            const _this = this
            const _globalId = globalId

            const query = this.publicPanoFeatureLayer.createQuery()
            query.where = 'GLOBALID = \'' + _globalId + '\''

            return dispatch('queryFeatureServiceByGlobalId', {
                featureLayer: this.publicPanoFeatureLayer,
                globalId: _globalId
            }).then((response) => {
                if (response.features.length === 0) {
                    return dispatch('queryFeatureServiceByGlobalId', {
                        featureLayer: this.securePanoFeatureLayer,
                        globalId: _globalId
                    })
                } else {
                    return response
                }
            }).then((response) => {
                if (response.features.length === 0) {
                    return
                }

                let item = response.features[0].attributes
                item.geometry = response.features[0].geometry
                item.layerUrl = response.features[0].layer.url
                item.globalid = globalId

                if (identityManager.findCredential(item.layerUrl)) {
                    item.token = identityManager.findCredential(item.layerUrl).token
                }

                if (item.UNIT_ID && item.UNIT_ID.length > 0) {
                    return dispatch('addUnitInfo', item).then((response) => {
                        commit('selectItem', {item: response.item})
                    }, () => {
                        commit('selectItem', {item: item})
                    })
                } else {
                    return dispatch('addPoiInfo', {
                        item: item,
                        searchDistance: this.poiInitialSearchDistance
                    }).then((response) => {
                        commit('selectItem', {item: response.item})
                    }, () => {
                        commit('selectItem', {item: item})
                    })
                }
            })
        },

        // eslint-disable-next-line no-unused-vars
        queryFeatureServiceByGlobalId({dispatch, commit}, queryParams) {
            const query = queryParams.featureLayer.createQuery()
            query.where = 'GLOBALID = \'' + queryParams.globalId + '\''

            return queryParams.featureLayer.queryFeatures(query)
        },

        // eslint-disable-next-line no-unused-vars
        addItem({dispatch, commit}, item) {
            item.NAME = Intl.DateTimeFormat().format(new Date(item.CAPTURED))
            if (item.UNIT_ID && item.UNIT_ID.length > 0) {
                return dispatch('addUnitInfo', item).then((response) => {
                    commit('addItem', {item: response.item})
                }, () => {
                    commit('addItem', {item: item})
                })
            } else {
                return dispatch('addPoiInfo', {
                    item: item,
                    searchDistance: this.poiInitialSearchDistance
                }).then((response) => {
                    commit('addItem', {item: response.item})
                }, () => {
                    commit('addItem', {item: item})
                })
            }
        },

        clearItems({commit}) {
            commit('clearItems')
        },

        // eslint-disable-next-line no-unused-vars
        addUnitInfo({dispatch, commit}, item) {
            commit('incrementActiveQueryCnt')
            const query = this.unitInfoFeatureLayer.createQuery()
            query.where = `UNIT_ID = '${item.UNIT_ID}'`

            return this.unitInfoFeatureLayer.queryFeatures(query).then((response) => {
                commit('decrementActiveQueryCnt')
                const unitName = response.features[0].attributes['NAME']
                if (unitName) item.NAME = unitName
                return {
                    item: item
                }
            }, () => {
                return {
                    item: item
                }
            })
        },

        // eslint-disable-next-line no-unused-vars
        addPoiInfo({dispatch, commit}, {item, searchDistance = 50}) {
            commit('incrementActiveQueryCnt')
            const query = this.poiInfoFeatureLayer.createQuery()
            query.where = 'LOCATIONTYPE IN (' + this.poiTypes.map(i => `'${i}'`).join(',') + ')'
            query.distance = searchDistance
            query.units = 'meters'
            query.geometry = item.geometry
            query.outFields = ['LOCATIONNAME']
            query.orderByFields = ['LOCATIONNAME']

            return this.poiInfoFeatureLayer.queryFeatures(query).then((response) => {
                commit('decrementActiveQueryCnt')
                let name = null

                if (response.features.length === 0 && searchDistance <= this.poiMaxSearchDistance) {
                    searchDistance += 50
                    return dispatch("addPoiInfo", {item, searchDistance}).then((response) => {
                        return {
                            item: response.item
                        }
                    })
                }

                response.features.forEach((feature) => {
                    if (name) name = name + ', ' + feature.attributes.LOCATIONNAME
                    if (!name) name = feature.attributes.LOCATIONNAME
                })

                if (name) {
                    item.NAME = name
                } else {
                    item.NAME = "Panoramic Photo Location"
                }

                return {
                    item: item
                }
            }, () => {
                return {
                    item: item
                }
            })
        },

        selectItem({commit}, item) {
            commit('selectItem', {item: item})
        },

        incrementActiveQueryCnt({commit}) {
            commit("incrementActiveQueryCnt")
        },

        decrementActiveQueryCnt({commit}) {
            commit("decrementActiveQueryCnt")
        }
    },

    mutations: {
        setItems(state, payload) {
            state.items = payload.items;
        },

        addItem(state, payload) {
            state.items.push(payload.item)
        },

        clearItems(state) {
            state.items = []
        },

        selectItem(state, payload) {
            state.selectedItem = payload.item
        },

        incrementActiveQueryCnt(state) {
            //debugger
            state.activeQueryCnt++
        },

        decrementActiveQueryCnt(state) {
            //debugger
            state.activeQueryCnt--
        }
    }
}
